import type {TextShapeObject} from '@PosterWhiteboard/items/text-item/text-shape.types';
import {
  TEXT_SHAPE_COVERAGE_MAX,
  TEXT_SHAPE_COVERAGE_MIN,
  TEXT_SHAPE_CURVE_AMOUNT_MAX,
  TEXT_SHAPE_CURVE_AMOUNT_MIN,
  TEXT_SHAPE_WAVE_AMOUNT_MAX,
  TEXT_SHAPE_WAVE_AMOUNT_MIN,
  TEXT_SHAPE_WAVE_COUNT_MAX,
  TEXT_SHAPE_WAVE_COUNT_MIN,
  TextShapeType,
} from '@PosterWhiteboard/items/text-item/text-shape.types';
import {assertNever} from '@Utils/assert-never.util';
import type {Point} from '@Utils/math.util';
import {getDistanceBetweenPoints} from '@Utils/math.util';
import type {Path, TMat2D} from '@postermywall/fabricjs-2';
import {iMatrix, Point as FabricPoint, util} from '@postermywall/fabricjs-2';
import clamp from 'lodash/clamp';
import round from 'lodash/round';

export interface TextShapePathInput {
  shape: TextShapeObject;
  /** The laid-out width of the text on one line, in the textbox's own units: the path is as long as it, but for the shapes the letters spread along. */
  textLength: number;
  /** The text's font size in the same units; sets the gap between a spiral's turns. */
  fontSize: number;
  /**
   * For a shape whose text runs round the outline of a figure (a circle, a square, a heart): the
   * room the figure may fill, in the same units, and it fills it — the box sets the figure's size,
   * as the design draws these shapes — unless the text needs a bigger one. Left out, the text
   * alone sizes the figure (a text slide, a box the shape sizes itself).
   */
  outlineFitBox?: TextShapeOutlineFitBox;
  /**
   * How many letters the text has, for a figure whose seam gap closes as the letters spread: the
   * letters either side of a heart's notch move away from it by half the spread each, so the gap
   * gives up as much. Left out, the gap stays open.
   */
  letterCount?: number;
}

export interface TextShapeOutlineFitBox {
  width: number;
  height: number;
}

/** A figure's outline: the size of the whole figure, where it lies in the path's own coordinates, and the length of the part of it the text runs along. */
export interface TextShapeOutline {
  width: number;
  height: number;
  /** The whole figure's box in the coordinates of the path data, which carries only the covered part of the outline. */
  bounds: TextShapeBounds;
  coveredLength: number;
}

export interface TextShapeBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

/** A figure's outline at unit scale: its size and where it lies, the length of its outline, how small it may be, and the part of it round the seam the text keeps clear of. */
interface OutlineFigure {
  width: number;
  height: number;
  left: number;
  top: number;
  perimeter: number;
  /** The outline is never shorter than this, in em: a heart needs the room for its lobes even under short text. */
  minPerimeterEm: number;
  /** The seam gap with the letters packed tight, in em: the letters either side of a heart's notch lean into it and would meet. */
  seamGapEm: number;
  /**
   * The seam gap once the letters are spread far enough apart not to meet, in em: a word space
   * at a heart's notch; none on a ring or a square, where the seam is then one gap like every
   * other (a fixed word space read as one gap wider than the rest on a four-letter ring, 2026-10-29).
   */
  seamGapSpreadEm: number;
}

/** A figure drawn as a polyline, read clockwise from the top. */
interface PolylineFigure extends OutlineFigure {
  points: Point[];
}

/** An arc to sample as points: its circle, the angles it runs between, and how many steps. */
interface ArcPointsInput {
  centre: Point;
  radius: number;
  fromAngle: number;
  toAngle: number;
  samples: number;
}

/** The circle that rounds a heart's point, and the angle on it where the right side runs into it. */
interface HeartPointRounding {
  centre: Point;
  fromAngle: number;
}

const FULL_TURN = Math.PI * 2;
/** Below this fraction of a turn a bend is as good as straight, and its radius would explode. */
const MIN_BEND_FRACTION = 0.01;
const PATH_FRACTION_DIGITS = 3;
/**
 * How far letters reach above their baseline, in em: a capital or an ascender in most faces. A
 * curve that hangs its text inside the ring widens the ring by this much, so the letter tops ride
 * the circle the text would fill and just touch there, as the baselines do on a ring the text
 * stands outside; the spare length at the baseline spreads the letters evenly. Hung on the ring
 * the text fills, the tops crowd into a blob (nine letters inside a 1.5 em ring, 2026-10-26), and
 * a fixed wider ring left short standing text far apart (2 em, 2026-10-26).
 */
const CURVE_HANGING_LETTER_HEIGHT_EM = 0.75;
const WAVE_SAMPLES_PER_PERIOD = 32;
/**
 * At full strength each hump of a wave is 0.8 as deep (peak to trough) as it is wide, so the
 * slider's end is the deepest wave whose glyphs, bent by the column warp the fork draws a wave
 * with, still read: steeper humps shear the letters into strokes (compared at 0.5, 0.75, 1.0 and
 * 1.25 on short, medium and long text, 2026-10-28). The count only adds humps: each is narrower
 * and, keeping its proportion, shallower, so a wave reads at every count and text length.
 */
const WAVE_HUMP_DEPTH_TO_WIDTH = 0.8;
/**
 * The gap at the seam of a closed outline, in em: about a word space, so a ring that closes reads
 * with a break between its last letter and its first, as between words, instead of running them
 * together ("NAMESONG", 2026-10-26). A heart's notch needs far more, see {@link HEART_NOTCH_GAP_EM}.
 */
const SEAM_GAP_EM = 0.3;
/**
 * The part of a full turn over which a closing curve's seam gap fades in, so the ring's size is
 * continuous at 100. The gap is capped at the arc the ring had open at the start of the fade, so
 * the ring never grows as it closes: for four big letters the open arc was narrower than a word
 * space, and a gap that size pushed the last letter away from the first as the slider closed
 * the ring (2026-10-29).
 */
const CURVE_SEAM_GAP_RAMP_TURN = 0.1;
/**
 * A ring or a square is never smaller than this radius (half side), in em. A short word's own
 * ring is smaller than its letters are wide — four letters at 141 px made a ring of 0.29 em, on
 * which the letters touched, the seam opened a hole of 117° and the flat glyphs fanned out
 * unevenly (2026-10-29) — so short text spreads round a ring at least this big instead. Chosen
 * over 0.75 and 1 em as the smallest that spaces a four-letter word evenly.
 */
const FIGURE_MIN_RADIUS_EM = 0.5;
/**
 * A heart's outline is never shorter than this, in em. Short text then spreads round the whole
 * heart, lobes included, instead of packing a tiny heart whose notch gap swallowed the lobes and
 * left a V (2026-10-27); text long enough to fill a bigger heart densely gets that heart. 15 over
 * the first 20, whose box was over half a 600 px page for a short phrase at 40 px, and over 12, at
 * which short text lost the lobes and read as a U (owner's call from renders, 2026-10-30).
 */
const HEART_MIN_PERIMETER_EM = 15;
/** A heart's lobes are circles of this radius, in the heart's own units. */
const HEART_LOBE_RADIUS = 0.5;
/**
 * How far either lobe's centre sits from the middle, in the heart's units. The lobes cross at the
 * notch, and the further apart they sit the deeper it dips: 0.48 dips it a fifth of the heart's
 * height. Shallower, as the Figma icon's 14 %, the top read as one arch once the text keeps clear of
 * the notch; the parametric heart before it dipped 24 % (owner's pick from renders, 2026-10-30).
 */
const HEART_LOBE_OFFSET = 0.48;
/** A heart's width over its height before its point is rounded: the classic heart's, a square stood on its corner with a half circle on each upper side. */
const HEART_ASPECT_RATIO = 1.094;
/**
 * The radius a heart's point is rounded with, in the heart's units. The parametric heart's cusp, and
 * a sharp point after it, stood the letters either side of the point back to back, the word space
 * between them gone ("musicfestival", 2026-10-30); round, the letters walk round it.
 */
const HEART_POINT_RADIUS = 0.3;
const HEART_LOBE_SAMPLES = 90;
const HEART_POINT_SAMPLES = 16;
/**
 * Text keeps out of the notch at the top of a heart: the covered outline starts and ends this far
 * from it, in em, split either side. Letters stand outside the outline, which at the notch means
 * into its V, so the letters either side of it lean into each other unless they sit about 1.5 em
 * along their arms. Spread letters sit half a spread further from the outline's ends, so the gap
 * closes by the spread (see `getSeamGap`), down to the seam's word space.
 */
const HEART_NOTCH_GAP_EM = 3;
const SQUARE_CORNER_RADIUS_FRACTION = 0.1;
const SQUARE_CORNER_SAMPLES = 8;
/** The gap between a spiral's turns, in em, so a turn clears the glyphs of the turn inside it. */
const SPIRAL_TURN_GAP_EM = 1.4;
/**
 * How far out from the centre the first letter sits, in em. The design starts the text at the
 * centre; half a font size in is as tight as the curl can hold a letter standing on its outside,
 * and tight enough that a short phrase visibly spirals instead of arching (a full font size in
 * read as a semicircle, 2026-10-27).
 */
const SPIRAL_START_RADIUS_EM = 0.5;
/** A spiral's path always runs at least this many turns, so short text shows where more text will go. */
const SPIRAL_MIN_TURNS = 1;
/** And always runs on past the text's last letter by this part of a turn, the preview of the next letters. */
const SPIRAL_PREVIEW_TURN_FRACTION = 0.25;
const SPIRAL_SAMPLE_STEP_RADIANS = 0.08;
const SPIRAL_SOLVE_ITERATIONS = 60;
/**
 * The tightest a drawn path may bend towards its letters, in em. The letters stand on the inside of
 * such a bend, so in a valley tighter than they are tall their tops crossed ("DEMON", 2026-10-30);
 * the layout eases the path round those bends. 1 em over 1.5 and 2 (owner's pick from renders,
 * 2026-10-30): the letters there may still touch, but the line keeps the shape the user drew. Bends
 * away from the letters stay as drawn, as the letters fan out there.
 */
const DRAWN_PATH_MIN_INNER_BEND_RADIUS_EM = 1;
/** How finely a drawn path is resampled to ease its bends, in em; never finer than one unit, nor into more than the most samples, so a tiny font or a very long stroke stays cheap. */
const DRAWN_PATH_SAMPLE_STEP_EM = 0.1;
const DRAWN_PATH_MIN_SAMPLE_STEP = 1;
const DRAWN_PATH_MAX_SAMPLES = 1500;
/** Each pass moves every point on too tight an inner bend this far towards its neighbours' midpoint, and the passes stop once none is, or after the most. */
const DRAWN_PATH_EASING_FRACTION = 0.5;
const DRAWN_PATH_MAX_EASING_PASSES = 2000;
/** How many eased drawn paths are kept: easing takes a few milliseconds and every layout of an item asks again. */
const EASED_DRAWN_PATH_CACHE_SIZE = 20;

/**
 * The SVG path the text of a shaped item follows, in the textbox's units and with the shape's
 * geometry centered where fabric expects it (fabric recenters a `Path` on its bounding box
 * anyway). The path is exactly as long as the text, so the text fills it edge to edge — except a
 * curve's that hangs its text inside the ring, which is wider by the letters' height
 * ({@link CURVE_HANGING_LETTER_HEIGHT_EM}), and a figure's (circle, square, heart) given a box
 * with more room than the text needs; those run longer than the text and the item spreads the
 * letters along them. `null` when the shape draws no path: NONE, or a bend/height of 0.
 */
export const getTextShapePathData = (input: TextShapePathInput): string | null => {
  const {shape, textLength, fontSize} = input;
  if (textLength <= 0) {
    return null;
  }
  switch (shape.type) {
    case TextShapeType.NONE:
      return null;
    case TextShapeType.CURVE:
      return getCurvePathData(shape.amount, textLength, fontSize);
    case TextShapeType.CIRCLE:
      return getCirclePathData(getFigureScaleInput(CIRCLE_FIGURE, input));
    case TextShapeType.WAVE:
      return getWavePathData(shape.amount, shape.count, textLength);
    case TextShapeType.SPIRAL:
      return getSpiralPathData(textLength, fontSize);
    case TextShapeType.HEART:
      return getFigurePathData(getFigureScaleInput(HEART_FIGURE, input));
    case TextShapeType.SQUARE:
      return getFigurePathData(getFigureScaleInput(SQUARE_FIGURE, input));
    case TextShapeType.PATH:
      return shape.path === null ? null : getDrawnPathDataForText(shape.path, fontSize);
    default:
      return assertNever(shape.type);
  }
};

/**
 * The figure a circle, square or heart shape draws for this input: the size of the whole figure
 * and how much of its outline the text runs along. Null for a shape that draws no figure.
 */
export const getTextShapeOutline = (pathInput: TextShapePathInput): TextShapeOutline | null => {
  const figure = getOutlineFigure(pathInput.shape.type);
  if (figure === null) {
    return null;
  }
  const input = getFigureScaleInput(figure, pathInput);
  const scale = getOutlineScale(input);
  return {
    width: figure.width * scale,
    height: figure.height * scale,
    bounds: {minX: figure.left * scale, minY: figure.top * scale, maxX: (figure.left + figure.width) * scale, maxY: (figure.top + figure.height) * scale},
    coveredLength: getCoveredLength(input, scale),
  };
};

/**
 * The length of outline the figure that just fills `outlineFitBox` gives text of this length —
 * the slider's share of its perimeter, clear of the seam — however long the text is. Null for a
 * shape that draws no figure.
 */
export const getOutlineRoomInBox = (pathInput: TextShapePathInput & {outlineFitBox: TextShapeOutlineFitBox}): number | null => {
  const figure = getOutlineFigure(pathInput.shape.type);
  if (figure === null) {
    return null;
  }
  return getCoveredLength(getFigureScaleInput(figure, pathInput), getOutlineFitScale(figure, pathInput.outlineFitBox));
};

/** The figure's scaling question for a shape input: the shape's slider is the figure's coverage. */
const getFigureScaleInput = <Figure extends OutlineFigure>(figure: Figure, {shape, textLength, fontSize, outlineFitBox, letterCount}: TextShapePathInput): FigureScaleInput & {figure: Figure} => {
  return {figure, coverage: shape.amount, textLength, fontSize, outlineFitBox, letterCount};
};

/** The figure a shape draws its text round, at unit scale; null for a shape that draws none. */
const getOutlineFigure = (type: TextShapeType): OutlineFigure | null => {
  switch (type) {
    case TextShapeType.CIRCLE:
      return CIRCLE_FIGURE;
    case TextShapeType.SQUARE:
      return SQUARE_FIGURE;
    case TextShapeType.HEART:
      return HEART_FIGURE;
    case TextShapeType.NONE:
    case TextShapeType.CURVE:
    case TextShapeType.WAVE:
    case TextShapeType.SPIRAL:
    case TextShapeType.PATH:
      return null;
    default:
      return assertNever(type);
  }
};

/**
 * A drawn path as its text follows it: the stroke as stored, with every bend towards the letters
 * tighter than {@link DRAWN_PATH_MIN_INNER_BEND_RADIUS_EM} eased round, so the letters standing
 * inside it do not cross. The stored path never changes, so a new font size eases it afresh, and a
 * stroke with no such bend comes back exactly as stored.
 */
const getDrawnPathDataForText = (pathData: string, fontSize: number): string => {
  const cacheKey = `${fontSize} ${pathData}`;
  const cachedPathData = easedDrawnPathCache.get(cacheKey);
  if (cachedPathData !== undefined) {
    easedDrawnPathCache.delete(cacheKey);
    easedDrawnPathCache.set(cacheKey, cachedPathData);
    return cachedPathData;
  }
  const easedPathData = getDrawnPathDataWithInnerBendsEased(pathData, fontSize);
  easedDrawnPathCache.set(cacheKey, easedPathData);
  const oldestCacheKey = easedDrawnPathCache.keys().next().value;
  if (easedDrawnPathCache.size > EASED_DRAWN_PATH_CACHE_SIZE && oldestCacheKey !== undefined) {
    easedDrawnPathCache.delete(oldestCacheKey);
  }
  return easedPathData;
};

/** Eased drawn paths by font size and stroke, least recently used first. */
const easedDrawnPathCache = new Map<string, string>();

const getDrawnPathDataWithInnerBendsEased = (pathData: string, fontSize: number): string => {
  const minRadius = DRAWN_PATH_MIN_INNER_BEND_RADIUS_EM * fontSize;
  const points = getEvenlySpacedPathPoints(pathData, fontSize);
  if (!points.some((_point, index): boolean => isInnerBendTooTight(points, index, minRadius))) {
    return pathData;
  }
  return getWholePolylinePathData(getPointsWithInnerBendsEased(points, minRadius), 1);
};

/** A path resampled evenly along its length, a tenth of an em apart, both ends included. */
const getEvenlySpacedPathPoints = (pathData: string, fontSize: number): Point[] => {
  const simplePath = util.makePathSimpler(util.parsePath(pathData));
  const segments = util.getPathSegmentsInfo(simplePath);
  const length = segments.reduce((sum, segment) => sum + segment.length, 0);
  const sampleCount = Math.max(1, Math.ceil(length / Math.max(DRAWN_PATH_SAMPLE_STEP_EM * fontSize, DRAWN_PATH_MIN_SAMPLE_STEP, length / DRAWN_PATH_MAX_SAMPLES)));
  const points: Point[] = [];
  for (let sampleIndex = 0; sampleIndex <= sampleCount; sampleIndex++) {
    const point = util.getPointOnPath(simplePath, (length * sampleIndex) / sampleCount, segments);
    if (point !== undefined) {
      points.push({x: point.x, y: point.y});
    }
  }
  return points;
};

/**
 * The points with every inner bend tighter than `minRadius` eased: each pass moves each such point
 * part of the way to its neighbours' midpoint, which spreads the turn over the points either side,
 * until no bend is too tight. The ends stay where they were drawn.
 */
const getPointsWithInnerBendsEased = (points: Point[], minRadius: number): Point[] => {
  const easedPoints = points.map((point): Point => ({...point}));
  for (let pass = 0; pass < DRAWN_PATH_MAX_EASING_PASSES; pass++) {
    let hasEasedABend = false;
    for (let index = 1; index < easedPoints.length - 1; index++) {
      if (isInnerBendTooTight(easedPoints, index, minRadius)) {
        const neighboursMidpoint = interpolatePoint(easedPoints[index - 1], easedPoints[index + 1], 0.5);
        easedPoints[index] = interpolatePoint(easedPoints[index], neighboursMidpoint, DRAWN_PATH_EASING_FRACTION);
        hasEasedABend = true;
      }
    }
    if (!hasEasedABend) {
      break;
    }
  }
  return easedPoints;
};

/**
 * Whether the path turns at `points[index]` towards its letters more sharply than a bend of
 * `minRadius` would over the same step. Fabric stands the letters on the left of the direction of
 * travel, which, with y pointing down, is a negative turn. The ends never turn.
 */
const isInnerBendTooTight = (points: Point[], index: number, minRadius: number): boolean => {
  if (index <= 0 || index >= points.length - 1) {
    return false;
  }
  const arriving = {x: points[index].x - points[index - 1].x, y: points[index].y - points[index - 1].y};
  const leaving = {x: points[index + 1].x - points[index].x, y: points[index + 1].y - points[index].y};
  const turn = Math.atan2(arriving.x * leaving.y - arriving.y * leaving.x, arriving.x * leaving.x + arriving.y * leaving.y);
  const step = (Math.hypot(arriving.x, arriving.y) + Math.hypot(leaving.x, leaving.y)) / 2;
  return turn < -step / minRadius;
};

/** The length of a path fabric has measured (`setPathInfo`), 0 before it has. */
export const getPathTotalLength = (path: Path): number => {
  const lastSegment = path.segmentsInfo?.at(-1);
  return lastSegment?.length ?? 0;
};

export interface DrawnPathInItemUnits {
  /** The stroke's commands in the item's own units, with their bounding box moved to the origin. */
  path: string;
  /** Where that origin must render on the page for the text to run along the stroke as it was drawn. */
  left: number;
  top: number;
}

/**
 * The SVG path commands fabric's pencil brush produces (`M`, `L`, `Q`: every number pair a point),
 * drawn in page units, as the item that will follow them stores
 * them: in its own units — page units undone through the item's matrix, translation left out,
 * since the item draws its path through its scale, rotation and flips — with their bounding box
 * moved to the origin. `null` for a path that never moves off one point, or an item squashed flat.
 */
export const getDrawnPathInItemUnits = (commands: (string | number)[][], itemTransform: TMat2D = iMatrix): DrawnPathInItemUnits | null => {
  if (isSquashedFlat(itemTransform)) {
    return null;
  }
  const toItemUnits = util.invertTransform(itemTransform);
  const localCommands = commands.map(([letter, ...numbers]) => {
    const localNumbers: number[] = [];
    for (let index = 0; index + 1 < numbers.length; index += 2) {
      const local = new FabricPoint(Number(numbers[index]), Number(numbers[index + 1])).transform(toItemUnits, true);
      localNumbers.push(local.x, local.y);
    }
    return {letter: String(letter), numbers: localNumbers};
  });
  const xs = localCommands.flatMap(({numbers}) => numbers.filter((_value, index) => index % 2 === 0));
  const ys = localCommands.flatMap(({numbers}) => numbers.filter((_value, index) => index % 2 === 1));
  if (xs.length === 0) {
    return null;
  }
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  if (Math.max(...xs) - minX < 1 && Math.max(...ys) - minY < 1) {
    return null;
  }
  const path = localCommands
    .map(({letter, numbers}) => [letter, ...numbers.map((value, index) => formatCompactNumber(value - (index % 2 === 0 ? minX : minY)))].join(' '))
    .join(' ');
  const originOnPage = new FabricPoint(minX, minY).transform(itemTransform, true);
  return {path, left: originOnPage.x, top: originOnPage.y};
};

/** A matrix that maps the plane onto a line or a point cannot be undone. */
const isSquashedFlat = ([a, b, c, d]: TMat2D): boolean => {
  return Math.abs(a * d - b * c) < Number.EPSILON;
};

/**
 * The gap a curve leaves at its seam, in the text's units: a word space once the curve closes
 * into a ring at ±100, fading in over the last {@link CURVE_SEAM_GAP_RAMP_TURN} of the turn so the
 * ring grows smoothly into it (a gap that appeared at 100 alone made the ring jump between 99 and
 * 100), nothing while its ends are further apart. Never more than the arc the ring had open where
 * the fade starts, so closing the ring only ever brings its ends closer. The ring's circumference
 * holds the gap, and the item starts the text half of it past the seam.
 */
export const getCurveSeamGap = (amount: number, fontSize: number, textLength: number): number => {
  const closing = clamp((getCurveTurnFraction(amount) - (1 - CURVE_SEAM_GAP_RAMP_TURN)) / CURVE_SEAM_GAP_RAMP_TURN, 0, 1);
  const openArcWhereTheFadeStarts = Math.max(textLength, 0) * (1 / (1 - CURVE_SEAM_GAP_RAMP_TURN) - 1);
  return closing * Math.min(SEAM_GAP_EM * Math.max(fontSize, 0), openArcWhereTheFadeStarts);
};

/**
 * The slider is the part of a turn the text sweeps: ±50 wraps it round half a circle, ±100 closes
 * the circle, whatever the text's length. Positive bends up (text over an arch), negative bends
 * down (text hanging inside the ring).
 */
const getCurvePathData = (amount: number, textLength: number, fontSize: number): string | null => {
  const radius = getCurveRadius(amount, textLength, fontSize);
  if (radius === null) {
    return getStraightLinePathData(-textLength / 2, textLength);
  }
  const sweptAngle = getCurveTurnFraction(amount) * FULL_TURN;
  return getArcPathData({radius, startAngle: -sweptAngle / 2, endAngle: sweptAngle / 2, bendsDown: amount < 0});
};

/**
 * The radius of the circle a curve of this strength bends text of this length round, centred on
 * the curve path's origin: the one the text fills edge to edge (with the seam gap, once it
 * closes) when it stands outside the ring, widened by the letters' height when it hangs inside,
 * so the letter tops ride that circle instead. Null while the curve is as good as straight.
 */
export const getCurveRadius = (amount: number, textLength: number, fontSize: number): number | null => {
  const turnFraction = getCurveTurnFraction(amount);
  if (turnFraction < MIN_BEND_FRACTION || textLength <= 0) {
    return null;
  }
  const filledRadius = (textLength + getCurveSeamGap(amount, fontSize, textLength)) / (turnFraction * FULL_TURN);
  const hangsInside = amount < 0;
  return hangsInside ? filledRadius + CURVE_HANGING_LETTER_HEIGHT_EM * Math.max(fontSize, 0) : filledRadius;
};

const getCurveTurnFraction = (amount: number): number => {
  return Math.abs(clamp(amount, TEXT_SHAPE_CURVE_AMOUNT_MIN, TEXT_SHAPE_CURVE_AMOUNT_MAX)) / TEXT_SHAPE_CURVE_AMOUNT_MAX;
};

/**
 * The text runs clockwise from the left of a circle, half the seam gap past it, spanning
 * `coverage` percent of the rest of the circumference: a full ring seams on its left, where the
 * design starts it (as a square starts at its top-left corner), and half a ring arches over the
 * top, reading left to right.
 */
const getCirclePathData = (input: FigureScaleInput): string => {
  const scale = getOutlineScale(input);
  const radius = scale / 2;
  const seamGapAngle = radius > 0 ? Math.min(FULL_TURN, getSeamGap(input, scale) / radius) : 0;
  const startAngle = -FULL_TURN / 4 + seamGapAngle / 2;
  return getArcPathData({radius, startAngle, endAngle: startAngle + getCoverageFraction(input.coverage) * (FULL_TURN - seamGapAngle), bendsDown: false});
};

interface ArcPathInput {
  radius: number;
  /** Angles are measured from the top of the circle, increasing clockwise. */
  startAngle: number;
  endAngle: number;
  /** Mirrors the arc below its chord, so the text hangs under an arch instead of sitting on one. */
  bendsDown: boolean;
}

/**
 * Two half arcs instead of one, so a full turn (coinciding end points) and any sweep over a half
 * turn stay unambiguous without the large-arc flag.
 */
const getArcPathData = ({radius, startAngle, endAngle, bendsDown}: ArcPathInput): string => {
  const verticalSign = bendsDown ? -1 : 1;
  const sweepFlag = bendsDown ? 0 : 1;
  const pointAt = (angle: number): Point => {
    return {x: radius * Math.sin(angle), y: -verticalSign * radius * Math.cos(angle)};
  };
  const midAngle = (startAngle + endAngle) / 2;
  const arcTo = (angle: number): string => {
    return `A ${formatNumber(radius)} ${formatNumber(radius)} 0 0 ${sweepFlag} ${formatPoint(pointAt(angle))}`;
  };
  return `M ${formatPoint(pointAt(startAngle))} ${arcTo(midAngle)} ${arcTo(endAngle)}`;
};

/** The wave the fork bends a text's flat line by: see the fork's `TWaveWarp`. Null when the wave is flat. */
export interface TextWaveWarp {
  amplitude: number;
  periods: number;
  startsWithTrough: boolean;
}

/**
 * The wave for a text of this length: `count` full sine periods across the text's own width, each
 * hump {@link WAVE_HUMP_DEPTH_TO_WIDTH} as deep as it is wide at 100 and the slider scaling
 * within it; 0 is flat; a positive strength starts with a peak and a negative one with a trough,
 * as the design draws it. The text keeps its width: the wave's path is the sine the glyphs are
 * warped by and the guide drawn under them, not a line the glyphs run along.
 */
export const getTextWaveWarp = (amount: number, count: number, textLength: number): TextWaveWarp | null => {
  const clampedAmount = clamp(amount, TEXT_SHAPE_WAVE_AMOUNT_MIN, TEXT_SHAPE_WAVE_AMOUNT_MAX);
  if (clampedAmount === 0 || textLength <= 0) {
    return null;
  }
  const periods = Math.round(clamp(count, TEXT_SHAPE_WAVE_COUNT_MIN, TEXT_SHAPE_WAVE_COUNT_MAX));
  const humpWidth = textLength / (2 * periods);
  const amplitude = ((WAVE_HUMP_DEPTH_TO_WIDTH * humpWidth) / 2) * (Math.abs(clampedAmount) / TEXT_SHAPE_WAVE_AMOUNT_MAX);
  return {amplitude, periods, startsWithTrough: clampedAmount < 0};
};

/** The wave's sine as a path across the text's width, sampled into chords, for the guide and the box. */
const getWavePathData = (amount: number, count: number, textLength: number): string | null => {
  const warp = getTextWaveWarp(amount, count, textLength);
  if (warp === null) {
    return getStraightLinePathData(0, textLength);
  }
  const sampleCount = WAVE_SAMPLES_PER_PERIOD * warp.periods;
  const sign = warp.startsWithTrough ? 1 : -1;
  const points: Point[] = [];
  for (let sampleIndex = 0; sampleIndex <= sampleCount; sampleIndex++) {
    const along = sampleIndex / sampleCount;
    points.push({x: along * textLength, y: sign * warp.amplitude * Math.sin(FULL_TURN * warp.periods * along)});
  }
  return getWholePolylinePathData(points, 1);
};

/**
 * An Archimedean spiral read from the centre outward, clockwise. Its turn gap is a fixed number
 * of ems so no turn runs over the glyphs of the turn inside it. The text runs from the centre
 * and the path runs on past it — at least {@link SPIRAL_MIN_TURNS} turn, and always
 * {@link SPIRAL_PREVIEW_TURN_FRACTION} of a turn past the last letter — so the spiral shows where
 * more text will go; more text winds it further out instead of shrinking the font.
 */
const getSpiralPathData = (textLength: number, fontSize: number): string | null => {
  if (fontSize <= 0) {
    return null;
  }
  const radiusPerRadian = (SPIRAL_TURN_GAP_EM * fontSize) / FULL_TURN;
  const startParameter = (SPIRAL_START_RADIUS_EM * fontSize) / radiusPerRadian;
  const textEndParameter = solveSpiralEndParameter(radiusPerRadian, startParameter, textLength);
  const pathEndParameter = Math.max(textEndParameter + SPIRAL_PREVIEW_TURN_FRACTION * FULL_TURN, startParameter + SPIRAL_MIN_TURNS * FULL_TURN);
  const textPoints = getSpiralPoints(radiusPerRadian, startParameter, textEndParameter);
  const previewPoints = getSpiralPoints(radiusPerRadian, textEndParameter, pathEndParameter).slice(1);
  const textPolylineLength = getPolylineLength(textPoints);
  if (textPolylineLength <= 0) {
    return null;
  }
  // The sampled spiral runs a hair shorter than the true one; scaling makes the text's part exactly as long as the text.
  return getWholePolylinePathData([...textPoints, ...previewPoints], textLength / textPolylineLength);
};

/** Samples of the spiral from one parameter to another, both ends included. */
const getSpiralPoints = (radiusPerRadian: number, fromParameter: number, toParameter: number): Point[] => {
  const points: Point[] = [];
  for (let parameter = fromParameter; parameter < toParameter; parameter += SPIRAL_SAMPLE_STEP_RADIANS) {
    points.push(spiralPointAt(radiusPerRadian, parameter));
  }
  points.push(spiralPointAt(radiusPerRadian, toParameter));
  return points;
};

const spiralPointAt = (radiusPerRadian: number, parameter: number): Point => {
  const radius = radiusPerRadian * parameter;
  return {x: radius * Math.cos(parameter), y: radius * Math.sin(parameter)};
};

/** Arc length of r = b·t from the origin to `parameter`. */
const getSpiralArcLength = (radiusPerRadian: number, parameter: number): number => {
  return (radiusPerRadian / 2) * (parameter * Math.sqrt(1 + parameter * parameter) + Math.asinh(parameter));
};

/** The parameter at which the spiral has run `length` past `startParameter`; the arc length is monotonic, so bisection suffices. */
const solveSpiralEndParameter = (radiusPerRadian: number, startParameter: number, length: number): number => {
  const targetArcLength = getSpiralArcLength(radiusPerRadian, startParameter) + length;
  let low = startParameter;
  // The spiral never runs slower than its starting radius, so this bound is always past the target.
  let high = startParameter + length / (radiusPerRadian * startParameter) + 1;
  for (let iteration = 0; iteration < SPIRAL_SOLVE_ITERATIONS; iteration++) {
    const mid = (low + high) / 2;
    if (getSpiralArcLength(radiusPerRadian, mid) < targetArcLength) {
      low = mid;
    } else {
      high = mid;
    }
  }
  return (low + high) / 2;
};

interface FigureScaleInput {
  figure: OutlineFigure;
  coverage: number;
  textLength: number;
  fontSize: number;
  outlineFitBox: TextShapeOutlineFitBox | undefined;
  letterCount: number | undefined;
}

/**
 * The covered part of a polyline figure's outline — a heart read clockwise from the notch at the
 * top, a rounded square from the start of its top edge — at the figure's scale, from half the
 * seam gap past the figure's start.
 */
const getFigurePathData = (input: FigureScaleInput & {figure: PolylineFigure}): string | null => {
  const {figure, coverage} = input;
  const scale = getOutlineScale(input);
  if (scale <= 0) {
    return null;
  }
  const seamGap = Math.min(figure.perimeter, getSeamGap(input, scale) / scale);
  const fromLength = seamGap / 2;
  return getPolylinePathData(figure.points, scale, fromLength, fromLength + getCoverageFraction(coverage) * (figure.perimeter - seamGap));
};

/**
 * The scale of a figure: the text's own need — the covered part of the outline exactly as long as
 * the text with the seam gap open, or the figure's smallest size — or the biggest figure the fit
 * box holds, whichever is larger.
 */
const getOutlineScale = ({figure, coverage, textLength, fontSize, outlineFitBox}: FigureScaleInput): number => {
  const neededScale = Math.max(textLength / getCoverageFraction(coverage) + figure.seamGapEm * fontSize, figure.minPerimeterEm * fontSize) / figure.perimeter;
  return Math.max(neededScale, outlineFitBox === undefined ? 0 : getOutlineFitScale(figure, outlineFitBox));
};

/** The length of outline a figure at `scale` gives its text: the slider's share of what is left clear of the seam gap. */
const getCoveredLength = (input: FigureScaleInput, scale: number): number => {
  return getCoverageFraction(input.coverage) * Math.max(0, input.figure.perimeter * scale - getSeamGap(input, scale));
};

/**
 * The seam gap of a figure at `scale`, in the text's units: open with the letters packed tight,
 * and closing by the spread the letters get once it has — the letters either side of the seam
 * then sit half a spread further from the outline's ends, as far from the seam as packed ones —
 * down to the spread gap. The spread is solved with the gap it makes: the room past the text and
 * the open gap, over the letters less the coverage's share of the gap given back.
 */
const getSeamGap = ({figure, coverage, textLength, fontSize, letterCount}: FigureScaleInput, scale: number): number => {
  const openGap = figure.seamGapEm * fontSize;
  const closedGap = figure.seamGapSpreadEm * fontSize;
  if (letterCount === undefined || letterCount < 2 || openGap <= closedGap) {
    return openGap;
  }
  const coverageFraction = getCoverageFraction(coverage);
  const roomBeforeGap = coverageFraction * figure.perimeter * scale;
  const spread = (roomBeforeGap - coverageFraction * openGap - textLength) / (letterCount - coverageFraction);
  return clamp(openGap - spread, closedGap, openGap);
};

/** The scale at which a figure just fills the box, on whichever side limits it; 0 for a box with no room. */
const getOutlineFitScale = (figure: OutlineFigure, outlineFitBox: TextShapeOutlineFitBox): number => {
  return Math.max(0, Math.min(outlineFitBox.width / figure.width, outlineFitBox.height / figure.height));
};

/**
 * The part of a polyline between two distances along it, at `scale`, as line segments: the
 * points between them, with the ends cut where the distances fall.
 */
const getPolylinePathData = (points: Point[], scale: number, fromLength: number, toLength: number): string | null => {
  const cumulativeLengths = getCumulativeLengths(points);
  const totalLength = cumulativeLengths[cumulativeLengths.length - 1];
  if (totalLength <= 0 || toLength <= fromLength) {
    return null;
  }
  const commands = [`M ${formatPoint(scalePoint(getPointAtPolylineLength(points, cumulativeLengths, fromLength), scale))}`];
  for (let pointIndex = 1; pointIndex < points.length; pointIndex++) {
    if (cumulativeLengths[pointIndex] <= fromLength) {
      continue;
    }
    if (cumulativeLengths[pointIndex] >= toLength) {
      commands.push(`L ${formatPoint(scalePoint(getPointAtPolylineLength(points, cumulativeLengths, toLength), scale))}`);
      break;
    }
    commands.push(`L ${formatPoint(scalePoint(points[pointIndex], scale))}`);
  }
  return commands.join(' ');
};

/** The point this far along a polyline, on the segment the distance falls in. */
const getPointAtPolylineLength = (points: Point[], cumulativeLengths: number[], length: number): Point => {
  for (let pointIndex = 1; pointIndex < points.length; pointIndex++) {
    if (cumulativeLengths[pointIndex] >= length) {
      const segmentLength = cumulativeLengths[pointIndex] - cumulativeLengths[pointIndex - 1];
      const fractionAlongSegment = segmentLength > 0 ? (length - cumulativeLengths[pointIndex - 1]) / segmentLength : 1;
      return interpolatePoint(points[pointIndex - 1], points[pointIndex], fractionAlongSegment);
    }
  }
  return points[points.length - 1];
};

/** Every point of a polyline, scaled, as line segments. */
const getWholePolylinePathData = (points: Point[], scale: number): string => {
  const [firstPoint, ...restPoints] = points;
  return [`M ${formatPoint(scalePoint(firstPoint, scale))}`, ...restPoints.map((point) => `L ${formatPoint(scalePoint(point, scale))}`)].join(' ');
};

const getCoverageFraction = (coverage: number): number => {
  return clamp(coverage, TEXT_SHAPE_COVERAGE_MIN, TEXT_SHAPE_COVERAGE_MAX) / TEXT_SHAPE_COVERAGE_MAX;
};

/**
 * A flat curve or wave: the text on one straight line as long as itself, from `startX`. A shape
 * whose slider is at 0 keeps its text on a path, so sliding through 0 never wraps the text back
 * into the plain box and out again (owner, 2026-10-29); only NONE wraps. Null for no text.
 */
const getStraightLinePathData = (startX: number, textLength: number): string | null => {
  if (textLength <= 0) {
    return null;
  }
  return getWholePolylinePathData(
    [
      {x: startX, y: 0},
      {x: startX + textLength, y: 0},
    ],
    1
  );
};

/** The distance along the polyline to each of its points, the first at 0. */
const getCumulativeLengths = (points: Point[]): number[] => {
  const cumulativeLengths = [0];
  for (let pointIndex = 1; pointIndex < points.length; pointIndex++) {
    cumulativeLengths.push(cumulativeLengths[pointIndex - 1] + getDistanceBetweenPoints(points[pointIndex - 1], points[pointIndex]));
  }
  return cumulativeLengths;
};

const getPolylineLength = (points: Point[]): number => {
  return getCumulativeLengths(points).at(-1) ?? 0;
};

const interpolatePoint = (from: Point, to: Point, fraction: number): Point => {
  return {x: from.x + (to.x - from.x) * fraction, y: from.y + (to.y - from.y) * fraction};
};

const scalePoint = (point: Point, scale: number): Point => {
  return {x: point.x * scale, y: point.y * scale};
};

/** A stored path's numbers, rounded like a generated path's but without the padding zeros the poster JSON would carry. */
const formatCompactNumber = (value: number): string => {
  return String(round(value, PATH_FRACTION_DIGITS));
};

const formatNumber = (value: number): string => {
  return value.toFixed(PATH_FRACTION_DIGITS);
};

/** A polyline figure's size and outline length, from its points. */
const getPolylineFigure = (points: Point[], gaps: Pick<OutlineFigure, 'minPerimeterEm' | 'seamGapEm' | 'seamGapSpreadEm'>): PolylineFigure => {
  const xs = points.map((point) => point.x);
  const ys = points.map((point) => point.y);
  const left = Math.min(...xs);
  const top = Math.min(...ys);
  return {...gaps, points, width: Math.max(...xs) - left, height: Math.max(...ys) - top, left, top, perimeter: getPolylineLength(points)};
};

/**
 * A heart drawn as the Figma's heart icon is: two round lobes crossing at the notch, and straight
 * sides running down from them to a rounded point. Read clockwise from the notch at the top, with
 * the lobes' centres on y = 0.
 */
const getHeartPoints = (): Point[] => {
  const notchY = -Math.sqrt(HEART_LOBE_RADIUS ** 2 - HEART_LOBE_OFFSET ** 2);
  const pointY = (2 * (HEART_LOBE_OFFSET + HEART_LOBE_RADIUS)) / HEART_ASPECT_RATIO - HEART_LOBE_RADIUS;
  const rightLobe = {x: HEART_LOBE_OFFSET, y: 0};
  const leftLobe = {x: -HEART_LOBE_OFFSET, y: 0};
  // The right side leaves its lobe where a line from the point just touches it, on the outer side.
  const sideAngle = Math.atan2(pointY, -HEART_LOBE_OFFSET) - Math.acos(HEART_LOBE_RADIUS / Math.hypot(HEART_LOBE_OFFSET, pointY));
  const sideTop = {x: rightLobe.x + HEART_LOBE_RADIUS * Math.cos(sideAngle), y: HEART_LOBE_RADIUS * Math.sin(sideAngle)};
  const rounding = getHeartPointRounding(sideTop, pointY);
  const notchAngle = Math.atan2(notchY, -HEART_LOBE_OFFSET) + FULL_TURN;
  return [
    ...getArcPoints({centre: rightLobe, radius: HEART_LOBE_RADIUS, fromAngle: notchAngle, toAngle: sideAngle + FULL_TURN, samples: HEART_LOBE_SAMPLES}),
    ...getArcPoints({centre: rounding.centre, radius: HEART_POINT_RADIUS, fromAngle: rounding.fromAngle, toAngle: Math.PI - rounding.fromAngle, samples: HEART_POINT_SAMPLES}),
    ...getArcPoints({centre: leftLobe, radius: HEART_LOBE_RADIUS, fromAngle: Math.PI - sideAngle, toAngle: 3 * Math.PI - notchAngle, samples: HEART_LOBE_SAMPLES}),
  ];
};

/** The circle that rounds a heart's point where its sides, running down from `sideTop` and its mirror, meet at `pointY`. */
const getHeartPointRounding = (sideTop: Point, pointY: number): HeartPointRounding => {
  const halfPointAngle = Math.atan2(sideTop.x, pointY - sideTop.y);
  const sideLength = Math.hypot(sideTop.x, pointY - sideTop.y);
  const roundingStartFraction = HEART_POINT_RADIUS / Math.tan(halfPointAngle) / sideLength;
  const roundingStart = {x: sideTop.x * roundingStartFraction, y: pointY - (pointY - sideTop.y) * roundingStartFraction};
  const centre = {x: 0, y: pointY - HEART_POINT_RADIUS / Math.sin(halfPointAngle)};
  return {centre, fromAngle: Math.atan2(roundingStart.y - centre.y, roundingStart.x - centre.x)};
};

/** The points of an arc, both ends included, clockwise from `fromAngle` to `toAngle`. */
const getArcPoints = ({centre, radius, fromAngle, toAngle, samples}: ArcPointsInput): Point[] => {
  return Array.from({length: samples + 1}, (_unused, sampleIndex): Point => {
    const angle = fromAngle + ((toAngle - fromAngle) * sampleIndex) / samples;
    return {x: centre.x + radius * Math.cos(angle), y: centre.y + radius * Math.sin(angle)};
  });
};

/** A unit square with rounded corners, read clockwise from the start of its top edge. */
const getSquarePoints = (): Point[] => {
  const cornerRadius = SQUARE_CORNER_RADIUS_FRACTION;
  const far = 1 - cornerRadius;
  const points: Point[] = [{x: cornerRadius, y: 0}];
  // Each corner is a quarter turn around its own centre, starting from the direction the edge arrived from.
  const corners: {center: Point; fromAngle: number}[] = [
    {center: {x: far, y: cornerRadius}, fromAngle: -Math.PI / 2},
    {center: {x: far, y: far}, fromAngle: 0},
    {center: {x: cornerRadius, y: far}, fromAngle: Math.PI / 2},
    {center: {x: cornerRadius, y: cornerRadius}, fromAngle: Math.PI},
  ];
  for (const corner of corners) {
    points.push(...getArcPoints({centre: corner.center, radius: cornerRadius, fromAngle: corner.fromAngle, toAngle: corner.fromAngle + Math.PI / 2, samples: SQUARE_CORNER_SAMPLES}));
  }
  points.push({x: cornerRadius, y: 0});
  return points;
};

/** A circle of unit diameter. */
const CIRCLE_FIGURE: OutlineFigure = {width: 1, height: 1, left: -0.5, top: -0.5, perimeter: Math.PI, minPerimeterEm: 2 * Math.PI * FIGURE_MIN_RADIUS_EM, seamGapEm: SEAM_GAP_EM, seamGapSpreadEm: 0};
const HEART_FIGURE = getPolylineFigure(getHeartPoints(), {minPerimeterEm: HEART_MIN_PERIMETER_EM, seamGapEm: HEART_NOTCH_GAP_EM, seamGapSpreadEm: SEAM_GAP_EM});
const SQUARE_POINTS = getSquarePoints();
const SQUARE_UNIT_PERIMETER = getCumulativeLengths(SQUARE_POINTS)[SQUARE_POINTS.length - 1];
/** The smallest square has the smallest radius as its half side, so its perimeter is the unit square's times that side. */
const SQUARE_FIGURE = getPolylineFigure(SQUARE_POINTS, {minPerimeterEm: SQUARE_UNIT_PERIMETER * 2 * FIGURE_MIN_RADIUS_EM, seamGapEm: SEAM_GAP_EM, seamGapSpreadEm: 0});

const formatPoint = (point: Point): string => {
  return `${formatNumber(point.x)} ${formatNumber(point.y)}`;
};
